import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Stack,
	Typography,
	cardHeaderClasses
} from '@mui/material';
import { Faq } from '@/pages/public/pricing/Faq';
import {
	useIsCollaborator,
	useIsExtendedCollaborator,
	usePaymentMethod,
	useShallowEqualSelector,
	useSubscribed
} from '@/hooks';
import { Button } from '@/components/Button';
import { showError } from '@/utils';
import routes from '@/routes';
import { fetchBillingInfo } from '@/store/auth/thunks';
import { clearData } from '@/store/billing';
import { usePlans } from '@/hooks/price';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { clearBillingData } from '@/store/auth';
import { SITE_NAME } from '@/config';
import { useMedia } from '@/hooks/responsive';
import { HANDLER_ID_PARAM } from '@/components/payment/adyen/constants';

import { TABS } from '../constants';
import { BillingHistory } from './BillingHistory';
import { ActivePlan } from './ActivePlan';
import { PaymentMethod } from './PaymentMethod';
import { Plans } from './Plans';
import { useUserPermissions } from '../../hooks/hooks';
import { UpdatePaymentMethod } from './UpdatePaymentMethod';
import { VIEWS_PERMISSIONS } from '../../users/constants';

export function Billing() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isSubscribed = useSubscribed();
	const { redirectIfNoPermissions } = useUserPermissions();
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();
	const smDown = useMedia('smDown');

	const [searchParams] = useSearchParams();

	const [updatePaymentMethodModalOpen, setUpdatePaymentMethodModalOpen] =
		useState(() => !!searchParams.get(HANDLER_ID_PARAM));

	const { hasPayments, subscriptionInfo } = useShallowEqualSelector(state => ({
		hasPayments: !state.billing?.isEmpty,
		subscriptionInfo: state.auth?.subscriptionInfo
	}));

	const { completed: ready, loading } = useSelector(
		state => state.auth.fetchBillingInfo
	);

	const plansData = usePlans();

	const { info } = usePaymentMethod();

	useEffect(() => {
		(async () => {
			try {
				await dispatch(fetchBillingInfo()).unwrap();
			} catch (error) {
				showError(error);
			}
		})();

		return () => {
			dispatch(clearData());
			dispatch(clearBillingData());
		};
	}, []);

	useEffect(() => {
		redirectIfNoPermissions(VIEWS_PERMISSIONS[1]);
	}, []);

	const isPending = subscriptionInfo?.status === SUBSCRIPTION_STATUS.PENDING;

	const shouldRenderPlans =
		!loading &&
		ready &&
		(!subscriptionInfo || (isPending && !hasPayments)) &&
		!isCollaborator &&
		!isExtendedCollaborator;

	const handleOpenUpdatePayment = () => {
		setUpdatePaymentMethodModalOpen(true);
	};

	const handleCloseUpdatePayment = () => {
		setUpdatePaymentMethodModalOpen(false);
	};

	const updatePaymentMethodRender = (
		<UpdatePaymentMethod
			open={updatePaymentMethodModalOpen}
			handleClose={handleCloseUpdatePayment}
			paymentInfo={info}
			subscriptionInfo={subscriptionInfo}
		/>
	);

	const top = (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			mb={{ xs: '8px', sm: '16px' }}
		>
			<Typography
				variant="h1"
				sx={{
					fontSize: { xs: 20, sm: 28 },
					lineHeight: { xs: '28px', sm: '32px' }
				}}
			>
				{t('billing.unsubscribedTitle', { siteName: SITE_NAME })}
			</Typography>
		</Stack>
	);

	if (loading || shouldRenderPlans) {
		return (
			<Box>
				{shouldRenderPlans && top}
				{updatePaymentMethodRender}
				{loading && (
					<Box textAlign="center">
						<CircularProgress size={50} sx={{ mt: 4 }} />
					</Box>
				)}
				{shouldRenderPlans && <Plans plansData={plansData} />}
			</Box>
		);
	}

	return (
		<Box>
			{updatePaymentMethodRender}

			<Stack
				spacing={{ xs: 1, sm: 4 }}
				direction={{ xs: 'column', sm: 'row' }}
				mt={3}
				sx={{
					[`.${cardHeaderClasses.title}`]: {
						fontSize: {
							xs: 14,
							sm: 16
						}
					}
				}}
				useFlexGap
			>
				{smDown && (
					<Card sx={{ flex: 1 }}>
						<CardHeader title={t('myAccount.taxInformation.title')} />
						<CardContent
							sx={{
								pt: 0
							}}
						>
							<Button
								variant="outlined"
								component={Link}
								to={routes.myAccount}
								state={{ tab: TABS.TAX_INFORMATION }}
							>
								{t('common.edit')}
							</Button>
						</CardContent>
					</Card>
				)}

				<ActivePlan
					subscriptionInfo={subscriptionInfo}
					paymentInfo={info}
					onClickUpdatePayment={handleOpenUpdatePayment}
				/>

				<PaymentMethod onClickUpdatePayment={handleOpenUpdatePayment} />
			</Stack>

			<BillingHistory mt={{ xs: 4, sm: 5 }} mb={{ xs: 6, sm: 8 }} />
			{!isSubscribed && <Faq />}
		</Box>
	);
}
